var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PauseRounded, PlayArrowRounded, PortableWifiOff, Refresh } from '@mui/icons-material';
import { CircularProgress, IconButton, Slider, Stack, Typography } from '@mui/material';
import { MapTransportBroadcastEventList } from 'app/pages/Map';
import { useAsyncAction } from 'app/services';
import { transportBroadcastRecordLoad } from 'app/store/map';
import { BroadcastPlayerEvents, canAttemptHevcMse, canAttemptHevcWebRtc, createBroadcastPlayer, getBroadcastDeviceId, grabVideoPosterDataUrl, isBroadcastPlayerSupported, resolveVideoAdapter, } from 'app/video';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
const playerControlSize = 40;
const playerIconButtonSx = {
    width: playerControlSize,
    height: playerControlSize,
    p: 0,
    color: 'common.white',
};
const playerControlSlotSx = {
    width: playerControlSize,
    height: playerControlSize,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
};
const MapTransportBroadcast = function MapTransportBroadcast() {
    var _a;
    const { transportBroadcastSelectedItem, transportSelectedDate } = useSelector((state) => state.map);
    const transportSelectedItem = useSelector((state) => state.map.transportSelectedItem);
    const broadcast = transportSelectedItem === null || transportSelectedItem === void 0 ? void 0 : transportSelectedItem.broadcast;
    const transportId = transportSelectedItem === null || transportSelectedItem === void 0 ? void 0 : transportSelectedItem.id;
    const events = (_a = broadcast === null || broadcast === void 0 ? void 0 : broadcast.events) !== null && _a !== void 0 ? _a : [];
    const online = broadcast === null || broadcast === void 0 ? void 0 : broadcast.online;
    const deviceId = getBroadcastDeviceId(broadcast);
    const videoAdapter = resolveVideoAdapter(broadcast);
    const broadcastRef = useRef(null);
    const broadcastPlayer = useRef(null);
    const activePlaybackRef = useRef(null);
    const intervalCurrentPosition = useRef(null);
    const [duration, setDuration] = useState(moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day') ? moment().diff(moment().startOf('day'), 'seconds') : 86399);
    const [marks, setMarks] = useState([]);
    const [position, setPosition] = useState(0);
    const [currentPosition, setCurrentPosition] = useState(moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day') ? moment().diff(moment().startOf('day'), 'seconds') : 0);
    const [broadcastPaused, setBroadcastPaused] = useState(false);
    const [broadcastStatus, setBroadcastStatus] = useState('idle');
    const [playerGeneration, setPlayerGeneration] = useState(0);
    const [retryHovered, setRetryHovered] = useState(false);
    const [eventChecked, setEventChecked] = useState(-1);
    const [channelError, setChannelError] = useState(null);
    const [rangeToken, setRangeToken] = useState(0);
    const currentPositionRef = useRef(currentPosition);
    const sawDateRef = useRef(false);
    currentPositionRef.current = currentPosition;
    const { asyncAction: fetchRecordData } = useAsyncAction(transportBroadcastRecordLoad);
    const handleEventExpandChange = (value) => {
        setPosition(value);
        setCurrentPosition(value);
    };
    const handleSliderChange = (_event, value) => {
        const nextValue = Array.isArray(value) ? value[0] : value;
        setPosition(nextValue);
        if (intervalCurrentPosition.current) {
            clearInterval(intervalCurrentPosition.current);
            intervalCurrentPosition.current = null;
        }
    };
    const handlePauseClick = () => {
        setBroadcastPaused(!broadcastPaused);
    };
    const handlePlayerRetry = () => {
        setBroadcastStatus('idle');
        setPlayerGeneration((value) => value + 1);
    };
    useEffect(() => {
        let cancelled = false;
        if (online === false) {
            setBroadcastStatus('rejected');
            setChannelError(null);
            return;
        }
        if (!videoAdapter || deviceId == null || !broadcastRef.current) {
            return;
        }
        if (videoAdapter.requiresFlvJs !== false && !isBroadcastPlayerSupported()) {
            return;
        }
        setBroadcastStatus('idle');
        setChannelError(null);
        const teardownPlayer = () => {
            if (!broadcastPlayer.current) {
                return;
            }
            broadcastPlayer.current.unload();
            broadcastPlayer.current.off();
            broadcastPlayer.current.detachMediaElement();
            broadcastPlayer.current = null;
        };
        const start = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const wantLive = moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day') && currentPosition >= duration - 30;
                if (!wantLive && videoAdapter.supportsArchive === false) {
                    if (!cancelled) {
                        setBroadcastStatus('rejected');
                        setChannelError('Архив для этого источника пока не поддерживается');
                    }
                    return;
                }
                let playback = null;
                let skipGenericReject = false;
                if (!wantLive) {
                    playback = yield videoAdapter.getArchivePlayback({
                        deviceId,
                        channelId: transportBroadcastSelectedItem,
                        positionSeconds: position,
                        selectedDate: transportSelectedDate,
                        transportId,
                    });
                    if (cancelled || !playback) {
                        if (!cancelled && !playback) {
                            setBroadcastStatus('rejected');
                            setChannelError('Видеопоток канала сейчас недоступен');
                        }
                        return;
                    }
                    if (cancelled || !broadcastRef.current) {
                        return;
                    }
                    activePlaybackRef.current = playback;
                    teardownPlayer();
                    broadcastPlayer.current = createBroadcastPlayer(playback);
                    broadcastPlayer.current.attachMediaElement(broadcastRef.current);
                    broadcastPlayer.current.on(BroadcastPlayerEvents.MEDIA_INFO, () => {
                        if (!cancelled) {
                            setBroadcastStatus('fulfilled');
                            setChannelError(null);
                        }
                    });
                    broadcastPlayer.current.on(BroadcastPlayerEvents.ERROR, (reason) => {
                        if (cancelled) {
                            return;
                        }
                        if (reason === 'NETWORK_ERROR' || reason === 'MEDIA_ERROR') {
                            setBroadcastStatus('rejected');
                            setChannelError('Видеопоток канала сейчас недоступен');
                        }
                    });
                    broadcastPlayer.current.load();
                    return;
                }
                const openLiveSession = (...args_1) => __awaiter(this, [...args_1], void 0, function* (attempt = 0) {
                    var _a, _b;
                    const rawPlayback = yield videoAdapter.getLivePlayback({
                        deviceId,
                        channelId: transportBroadcastSelectedItem,
                        transportId,
                    });
                    if (cancelled || !rawPlayback || !broadcastRef.current) {
                        return null;
                    }
                    const resolveHevcDelivery = (playback) => __awaiter(this, void 0, void 0, function* () {
                        var _a;
                        if (playback.kind !== 'mse' && playback.kind !== 'webrtc') {
                            return playback;
                        }
                        if (playback.kind === 'mse') {
                            if (yield canAttemptHevcMse()) {
                                return playback;
                            }
                            if (((_a = playback.fallback) === null || _a === void 0 ? void 0 : _a.kind) === 'webrtc' && (yield canAttemptHevcWebRtc())) {
                                return Object.assign(Object.assign({}, playback), { kind: 'webrtc', url: playback.fallback.url, strategy: playback.fallback.strategy || 'h265-webrtc', fallback: undefined });
                            }
                            return null;
                        }
                        if (playback.kind === 'webrtc' && !(yield canAttemptHevcWebRtc())) {
                            return null;
                        }
                        return playback;
                    });
                    const nextPlayback = yield resolveHevcDelivery(rawPlayback);
                    if (!nextPlayback) {
                        if (!cancelled) {
                            setBroadcastStatus('rejected');
                            setChannelError('Браузер не поддерживает H.265 (нужен Chrome/Edge с HEVC для MSE или WebRTC)');
                        }
                        skipGenericReject = true;
                        if (rawPlayback.streamName && transportId != null) {
                            yield ((_a = videoAdapter.releaseLive) === null || _a === void 0 ? void 0 : _a.call(videoAdapter, { transportId, streamName: rawPlayback.streamName }));
                        }
                        return null;
                    }
                    const startPlayer = (playbackToStart, allowWebrtcFallback) => {
                        activePlaybackRef.current = playbackToStart;
                        teardownPlayer();
                        if (!broadcastRef.current) {
                            return;
                        }
                        broadcastPlayer.current = createBroadcastPlayer(playbackToStart);
                        broadcastPlayer.current.attachMediaElement(broadcastRef.current);
                        broadcastPlayer.current.on(BroadcastPlayerEvents.MEDIA_INFO, () => {
                            if (cancelled) {
                                return;
                            }
                            setBroadcastStatus('fulfilled');
                            setChannelError(null);
                            const dataUrl = grabVideoPosterDataUrl(broadcastRef.current);
                            if (dataUrl && videoAdapter.livePosterBus) {
                                videoAdapter.livePosterBus.publish({ channelId: transportBroadcastSelectedItem, posterUrl: dataUrl });
                            }
                        });
                        broadcastPlayer.current.on(BroadcastPlayerEvents.ERROR, (reason) => {
                            var _a, _b;
                            if (cancelled) {
                                return;
                            }
                            // MSE failed after internal retries → same go2rtc source via WebRTC (still no server decode).
                            if (allowWebrtcFallback &&
                                playbackToStart.kind === 'mse' &&
                                ((_a = playbackToStart.fallback) === null || _a === void 0 ? void 0 : _a.kind) === 'webrtc' &&
                                (reason === 'NETWORK_ERROR' || reason === 'MEDIA_ERROR')) {
                                canAttemptHevcWebRtc().then((ok) => {
                                    if (cancelled || !ok) {
                                        if (!cancelled) {
                                            skipGenericReject = true;
                                            setBroadcastStatus('rejected');
                                            setChannelError('Не удалось открыть H.265 (MSE и WebRTC). Нужен браузер с HEVC');
                                        }
                                        return;
                                    }
                                    // MSE failed after internal retries → same go2rtc source via WebRTC (still no server decode).
                                    startPlayer(Object.assign(Object.assign({}, playbackToStart), { kind: 'webrtc', url: playbackToStart.fallback.url, strategy: playbackToStart.fallback.strategy || 'h265-webrtc', fallback: undefined }), false);
                                });
                                return;
                            }
                            // One full re-open (cold media / release race).
                            if (videoAdapter.retryLiveOnTransientError && attempt < 1 && (reason === 'NETWORK_ERROR' || reason === 'MEDIA_ERROR')) {
                                const failedName = playbackToStart.streamName;
                                teardownPlayer();
                                if (transportId != null && failedName) {
                                    (_b = videoAdapter.releaseLive) === null || _b === void 0 ? void 0 : _b.call(videoAdapter, { transportId, streamName: failedName });
                                }
                                window.setTimeout(() => {
                                    if (cancelled) {
                                        return;
                                    }
                                    openLiveSession(attempt + 1).catch(() => undefined);
                                }, 500);
                                return;
                            }
                            if (reason === 'NETWORK_ERROR' ||
                                reason === 'MEDIA_ERROR' ||
                                playbackToStart.kind === 'webrtc' ||
                                playbackToStart.kind === 'mse') {
                                skipGenericReject = true;
                                setBroadcastStatus('rejected');
                                setChannelError(playbackToStart.kind === 'webrtc'
                                    ? 'WebRTC/H.265 не удалось открыть. Нужен браузер с HEVC'
                                    : 'Не удалось открыть live. Попробуйте другой канал или обновить');
                            }
                        });
                        broadcastPlayer.current.load();
                    };
                    startPlayer(nextPlayback, Boolean(nextPlayback.kind === 'mse' && ((_b = nextPlayback.fallback) === null || _b === void 0 ? void 0 : _b.kind) === 'webrtc'));
                    return nextPlayback;
                });
                // Start live immediately — no frontend queue (previews must not block live).
                playback = yield openLiveSession();
                if (cancelled || !playback) {
                    if (!cancelled && !playback && !skipGenericReject) {
                        setBroadcastStatus('rejected');
                        setChannelError('Видеопоток канала сейчас недоступен');
                    }
                }
            }
            catch (error) {
                if (!cancelled) {
                    const data = error === null || error === void 0 ? void 0 : error.data;
                    setBroadcastStatus('rejected');
                    setChannelError((data === null || data === void 0 ? void 0 : data.code) === 'DAHUA_ARCHIVE_EMPTY' ? data.error || 'За выбранный период записей нет' : 'Видеопоток канала сейчас недоступен');
                }
            }
        });
        start();
        return () => {
            var _a, _b, _c;
            cancelled = true;
            (_a = videoAdapter.livePosterBus) === null || _a === void 0 ? void 0 : _a.publish(null);
            const streamName = (_b = activePlaybackRef.current) === null || _b === void 0 ? void 0 : _b.streamName;
            teardownPlayer();
            if (transportId != null && streamName) {
                (_c = videoAdapter.releaseLive) === null || _c === void 0 ? void 0 : _c.call(videoAdapter, { transportId, streamName });
            }
            activePlaybackRef.current = null;
        };
        // rangeToken (not the date itself) reopens playback after the seek/duration state has flushed.
    }, [deviceId, position, transportBroadcastSelectedItem, online, playerGeneration, transportId, rangeToken]);
    useEffect(() => {
        const nextMarks = events.map((broadcastEvent) => ({
            value: moment(broadcastEvent.time).diff(moment(broadcastEvent.time).startOf('day'), 'seconds'),
            label: broadcastEvent.type,
            alarmid: broadcastEvent.alarmid,
        }));
        setMarks(nextMarks);
    }, [events]);
    useEffect(() => {
        var _a;
        if (!broadcastRef.current) {
            return;
        }
        if (broadcastPaused) {
            broadcastRef.current.pause();
            return;
        }
        (_a = broadcastRef.current.play()) === null || _a === void 0 ? void 0 : _a.catch(() => undefined);
    }, [broadcastPaused]);
    useEffect(() => {
        const markIndex = marks.findIndex((mark) => mark.value > position - 300 && mark.value < position + 300);
        setEventChecked(markIndex !== -1 ? marks[markIndex].alarmid : markIndex);
    }, [position, marks]);
    useEffect(() => {
        const isCurrentRange = moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day');
        if (!sawDateRef.current) {
            sawDateRef.current = true;
            if (!isCurrentRange) {
                setDuration(86399);
            }
            return;
        }
        if (isCurrentRange) {
            const nowSec = moment().diff(moment().startOf('day'), 'seconds');
            setDuration(nowSec);
            setCurrentPosition(nowSec);
            setPosition(0);
        }
        else {
            const seek = Math.min(86399, Math.max(0, currentPositionRef.current || 0));
            setDuration(86399);
            setPosition(seek);
            setCurrentPosition(seek);
        }
        setRangeToken((value) => value + 1);
    }, [transportSelectedDate]);
    useEffect(() => {
        if (!moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day')) {
            return;
        }
        const intervalDuration = setInterval(() => {
            setDuration(moment().diff(moment().startOf('day'), 'seconds'));
        }, 1000);
        intervalCurrentPosition.current = setInterval(() => {
            setCurrentPosition(moment().diff(moment().startOf('day'), 'seconds'));
        }, 1000);
        return () => {
            clearInterval(intervalDuration);
            if (intervalCurrentPosition.current) {
                clearInterval(intervalCurrentPosition.current);
                intervalCurrentPosition.current = null;
            }
        };
    }, [transportSelectedDate]);
    useEffect(() => {
        if (online === false) {
            return;
        }
        fetchRecordData();
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', sx: (theme) => ({
                width: '100%',
                flex: '1 0 auto',
                padding: theme.spacing(0),
                position: 'relative',
                bgcolor: theme.palette.common.black,
                video: {
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                },
            }) },
            React.createElement("video", { ref: broadcastRef, autoPlay: true }),
            React.createElement(Stack, { direction: 'column', sx: (theme) => (Object.assign({ width: '100%', marginTop: 'auto', padding: theme.spacing(0, 3, 2), zIndex: '100', '.MuiSlider-thumb': {
                        width: '12px',
                        height: '12px',
                        bgcolor: theme.palette.common.white,
                        '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                            boxShadow: 'inherit',
                        },
                        '&:before': {
                            display: 'none',
                        },
                    }, '.MuiSlider-valueLabel': {
                        color: theme.palette.common.black,
                        backgroundColor: theme.palette.background.default,
                        marginBottom: '8px !important',
                        padding: '2px 6px',
                        fontSize: '10px',
                        '.MuiTooltip-arrow': {
                            color: theme.palette.background.default,
                        },
                    }, '.MuiSlider-markLabel': {
                        top: '-8px',
                        color: theme.palette.common.white,
                        bgcolor: theme.palette.warning.dark,
                        padding: '2px 6px',
                        fontSize: '10px',
                        borderRadius: theme.shape.borderRadius,
                    }, '.MuiSlider-mark': {
                        width: '6px',
                        marginLeft: '-2px',
                        bgcolor: theme.palette.warning.dark,
                    } }, (broadcastStatus === 'rejected' && {
                    display: 'none',
                }))) },
                React.createElement(Slider, { min: 0, marks: marks, max: duration, value: currentPosition, valueLabelDisplay: 'auto', valueLabelFormat: (v) => moment.utc(v * 1000).format('HH:mm:ss'), onChange: (_, value) => setCurrentPosition(Array.isArray(value) ? value[0] : value), onChangeCommitted: handleSliderChange, sx: (theme) => ({
                        color: theme.palette.common.white,
                        marginBottom: '6px',
                    }) }),
                React.createElement(Stack, { direction: 'row', alignItems: 'center', justifyContent: 'space-between', sx: (theme) => ({
                        marginTop: theme.spacing(-2),
                        '.MuiTypography-root': {
                            padding: '0',
                            color: theme.palette.background.paper,
                        },
                    }) },
                    React.createElement(Typography, null, moment.utc(position * 1000).format('HH:mm:ss')),
                    React.createElement(Typography, null, moment.utc(duration * 1000).format('HH:mm:ss')))),
            React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', sx: (theme) => (Object.assign({ position: 'absolute', bottom: '55px', right: '10px', bgcolor: theme.palette.common.black, svg: {
                        color: theme.palette.common.white,
                    }, '.MuiButtonBase-root': {
                        color: theme.palette.primary.contrastText,
                        svg: {
                            color: theme.palette.primary.contrastText,
                        },
                    } }, (broadcastStatus === 'fulfilled' && {
                    bgcolor: 'transparent',
                }))) },
                broadcastStatus === 'fulfilled' && (React.createElement(IconButton, { onClick: handlePauseClick, sx: playerIconButtonSx }, broadcastPaused ? React.createElement(PlayArrowRounded, { fontSize: 'medium' }) : React.createElement(PauseRounded, { fontSize: 'medium' }))),
                broadcastStatus === 'rejected' &&
                    (online === false ? (React.createElement(Stack, { sx: playerControlSlotSx },
                        React.createElement(PortableWifiOff, { fontSize: 'medium', sx: { color: 'common.white' } }))) : (React.createElement(IconButton, { onClick: handlePlayerRetry, onMouseEnter: () => setRetryHovered(true), onMouseLeave: () => setRetryHovered(false), sx: Object.assign(Object.assign({}, playerIconButtonSx), { '&:hover': {
                                bgcolor: 'action.hover',
                            } }) }, retryHovered ? React.createElement(Refresh, { fontSize: 'medium' }) : React.createElement(PortableWifiOff, { fontSize: 'medium' })))),
                broadcastStatus === 'idle' && (React.createElement(Stack, { sx: playerControlSlotSx },
                    React.createElement(CircularProgress, { size: 24, sx: { color: 'common.white' } })))),
            broadcastStatus === 'rejected' && channelError && online !== false && (React.createElement(Typography, { align: 'center', sx: {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'common.white',
                    px: 2,
                    zIndex: 2,
                } }, channelError))),
        React.createElement(Stack, { direction: 'row', alignItems: 'center', sx: {
                width: '100%',
                height: '200px',
                flex: '0 0 auto',
                borderRadius: '0',
                bgcolor: 'background.paper',
            } },
            events.length !== 0 && React.createElement(MapTransportBroadcastEventList, { checkedItem: eventChecked, onChangePlayTime: handleEventExpandChange }),
            events.length === 0 && (React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', sx: (theme) => ({
                    width: '100%',
                    height: '100%',
                    padding: theme.spacing(2),
                }) },
                React.createElement(Typography, { align: 'center', variant: 'body1', component: 'span' }, "\u0417\u0434\u0435\u0441\u044C \u043F\u043E\u043A\u0430 \u043D\u0438\u0447\u0435\u0433\u043E \u043D\u0435\u0442"),
                React.createElement(Typography, { align: 'center', color: 'text.secondary', variant: 'caption', component: 'span' }, "\u041D\u0430 \u0437\u0430\u0434\u0430\u043D\u043D\u0443\u044E \u0434\u0430\u0442\u0443 \u0441\u043E\u0431\u044B\u0442\u0438\u0439 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E"))))));
};
export default MapTransportBroadcast;
